import React, { useEffect, useMemo, useState } from 'react';
import { MapPinIcon } from 'lucide-react';
import type { QuestionRendererProps } from './types';
import { MapCanvas, type CityMarker } from '../../../visuals/components/topoMap/MapCanvas';
import { FALLBACK_COLOR } from './rendererUtils';
import citiesData from '../../../content/atlases/nl/cities.json';
import { useEdgeTriggeredSubmit } from './useEdgeTriggeredSubmit';
/**
 * MapLocatePointRenderer — Unified map click renderer.
 *
 * Modes:
 * - 'free' (default): Click anywhere on the map. Answer payload: JSON.stringify({ lat, lon }).
 * - 'snap-to-dot': Click on predefined city dots. Answer payload: string (cityId).
 *
 * Submit pattern: emits onAnswer once when shouldValidate flips to true.
 * No internal submit button — uses the global "Controleren" button.
 */
export function MapLocatePointRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate,
  moduleColor
}: QuestionRendererProps) {
  if (!question.data) return null;
  const color = moduleColor || FALLBACK_COLOR;
  const data = question.data as Record<string, unknown>;
  const mapId = String(data.mapId ?? '');
  if (!mapId) return null;
  // Mode detection with backward compatibility
  const explicitMode = String(data.mode || 'free');
  const isSnapToDot = explicitMode === 'snap-to-dot' || data.correctCityId && !data.targetPoint;
  // Free mode state
  const [pickedPoint, setPickedPoint] = useState<{
    lat: number;
    lon: number;
  } | null>(null);
  // Snap-to-dot mode state
  const [pickedCityId, setPickedCityId] = useState<string | null>(null);
  useEffect(() => {
    setPickedPoint(null);
    setPickedCityId(null);
  }, [question.id]);
  const isReady = isSnapToDot ? pickedCityId !== null : pickedPoint !== null;
  useEffect(() => {
    onReadyChange?.(isReady);
  }, [isReady, onReadyChange]);
  const answerPayload = useMemo(() => {
    if (isSnapToDot) return pickedCityId ?? '';
    return pickedPoint ? JSON.stringify(pickedPoint) : '';
  }, [isSnapToDot, pickedCityId, pickedPoint]);
  useEdgeTriggeredSubmit(answerPayload, shouldValidate, onAnswer, question.id);
  // --- Free Mode Data ---
  const referenceIds = Array.isArray(data.referenceCityIds) ? data.referenceCityIds as string[] : Array.isArray(data.showOtherCities) ? data.showOtherCities as string[] : [];
  const referenceCityMarkers: CityMarker[] = useMemo(() => {
    if (!mapId.startsWith('nl-') || referenceIds.length === 0) return [];
    const all = (citiesData?.cities ?? []) as Array<{
      id: string;
      name: string;
      lat: number;
      lon: number;
    }>;
    return all.filter(c => referenceIds.includes(c.id)).map(c => ({
      id: c.id,
      name: c.name,
      lat: c.lat,
      lon: c.lon
    }));
  }, [mapId, referenceIds]);
  // --- Snap-to-Dot Mode Data ---
  const focusRegionId = data.focusRegionId ? String(data.focusRegionId) : undefined;
  const candidateIds = Array.isArray(data.candidateCityIds) ? data.candidateCityIds as string[] : Array.isArray(data.cityIds) ? data.cityIds as string[] : [];
  const selectableCities: CityMarker[] = useMemo(() => {
    if (!mapId.startsWith('nl-') || candidateIds.length === 0) return [];
    const all = (citiesData?.cities ?? []) as Array<{
      id: string;
      name: string;
      lat: number;
      lon: number;
    }>;
    return all.filter(c => candidateIds.includes(c.id)).map(c => ({
      id: c.id,
      name: c.name,
      lat: c.lat,
      lon: c.lon
    }));
  }, [mapId, candidateIds]);
  return <div className="flex flex-col items-center gap-3 w-full" style={{
    touchAction: 'pan-y'
  }} data-id="element-11526">
      <div className="text-sm text-stone-500 flex items-center gap-1.5" data-id="element-11527">
        <MapPinIcon className="w-4 h-4" data-id="element-11528" />
        {isSnapToDot ? 'Klik op de juiste stad' : 'Klik op de juiste plek op de kaart'}
      </div>

      {isSnapToDot ? <MapCanvas mapId={mapId} mode="select-city" focusRegionId={focusRegionId} selectableCities={selectableCities} selectedCityId={pickedCityId} onCitySelect={setPickedCityId} width={420} height={420} ariaLabel={question.question} moduleColor={color} data-id="element-11529" /> : <MapCanvas mapId={mapId} mode="select-point" onPointSelect={setPickedPoint} selectedPoint={pickedPoint} cityMarkers={referenceCityMarkers} width={420} height={420} ariaLabel={question.question} moduleColor={color} data-id="element-11530" />}

      <p className="text-sm text-stone-500 flex items-center gap-1.5 min-h-[1.25rem]" aria-live="polite" data-id="element-11531">
        {isSnapToDot ? pickedCityId ? <>
              <span className="inline-block w-2 h-2 rounded-full" style={{
          backgroundColor: color
        }} aria-hidden="true" data-id="element-11532" />
              Stad gekozen — klik op een andere stip om te wisselen
            </> : <span className="text-stone-400" data-id="element-11533">Kies een van de stippen</span> : pickedPoint ? 'Pin geplaatst — klik nogmaals om te verplaatsen' : '\u00A0'}
      </p>
    </div>;
}